import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { CheckCircle, XCircle, GraduationCap, Calendar, Clock, User, FileText, CreditCard, Briefcase } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function VerificarDocumentoPage() {
  const { token } = useParams();
  const [verificacion, setVerificacion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    verificarDocumento();
  }, [token]);

  const verificarDocumento = async () => {
    try {
      const res = await axios.get(`${API_URL}/capacitaciones/verificar/${token}`);
      setVerificacion(res.data);
    } catch (err) {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires', day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  // Determinar si es un certificado de trámite o de curso
  const esCertificadoTramite = verificacion?.tipo === 'certificado_tramite';

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-6xl mx-auto px-4 py-4 flex justify-center">
          <Link to="/">
            <img src="/logo-colegio.png" alt="Logo" className="h-12 w-auto" />
          </Link>
        </div>
      </header>

      {/* Main */}
      <main className="max-w-lg mx-auto px-4 py-12">
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          {/* Status Header */}
          <div className={`p-8 text-center ${error ? 'bg-red-50' : 'bg-emerald-50'}`}>
            {error ? (
              <>
                <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <XCircle className="w-12 h-12 text-red-600" />
                </div>
                <h1 className="text-2xl font-bold text-red-800">Documento No Válido</h1>
                <p className="text-red-600 mt-2">
                  El código de verificación no corresponde a ningún documento emitido.
                </p>
              </>
            ) : (
              <>
                <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-12 h-12 text-emerald-600" />
                </div>
                <h1 className="text-2xl font-bold text-emerald-800">Documento Válido</h1>
                <p className="text-emerald-600 mt-2">
                  Este documento fue emitido oficialmente por el Colegio.
                </p>
              </>
            )}
          </div>

          {/* Document Details */}
          {verificacion && (
            <div className="p-6 space-y-4">
              {/* Tipo de documento */}
              <div className="border-b pb-4">
                <p className="text-sm text-gray-500 mb-1">Tipo de documento</p>
                <p className="font-semibold text-gray-900 flex items-center gap-2">
                  {esCertificadoTramite ? (
                    <>
                      <Briefcase className="w-5 h-5 text-gray-400" />
                      {verificacion.tipo_tramite_texto || 'Certificado de Trámite'}
                    </>
                  ) : (
                    <>
                      <FileText className="w-5 h-5 text-gray-400" />
                      {verificacion.tipo === 'constancia' 
                        ? 'Constancia de Asistencia' 
                        : verificacion.tipo === 'certificado_disertante'
                        ? 'Certificado de Disertante'
                        : 'Certificado de Capacitación'}
                    </>
                  )}
                </p>
              </div>

              {/* Titular */}
              <div className="border-b pb-4">
                <p className="text-sm text-gray-500 mb-1">Titular</p>
                <p className="font-semibold text-gray-900 flex items-center gap-2">
                  <User className="w-5 h-5 text-gray-400" />
                  {verificacion.nombre} {verificacion.apellido}
                </p>
                {verificacion.rol && (
                  <p className="text-sm text-gray-600 mt-1 ml-7">
                    Participó en calidad de: <span className="font-medium">{verificacion.rol}</span>
                  </p>
                )}
              </div>

              {/* DNI (solo para trámites) */}
              {esCertificadoTramite && verificacion.dni && (
                <div className="border-b pb-4">
                  <p className="text-sm text-gray-500 mb-1">DNI</p>
                  <p className="font-semibold text-gray-900 flex items-center gap-2">
                    <CreditCard className="w-5 h-5 text-gray-400" />
                    {verificacion.dni}
                  </p>
                </div>
              )}

              {/* Matrícula Profesional (solo para trámites) */}
              {esCertificadoTramite && verificacion.mp_numero && (
                <div className="border-b pb-4">
                  <p className="text-sm text-gray-500 mb-1">Matrícula Profesional</p>
                  <p className="font-semibold text-gray-900 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-gray-400" />
                    {verificacion.mp_numero}
                  </p>
                </div>
              )}

              {/* Curso (solo para capacitaciones) */}
              {!esCertificadoTramite && verificacion.curso_nombre && (
                <div className="border-b pb-4">
                  <p className="text-sm text-gray-500 mb-1">Curso</p>
                  <p className="font-semibold text-gray-900 flex items-center gap-2">
                    <GraduationCap className="w-5 h-5 text-gray-400" />
                    {verificacion.curso_nombre}
                  </p>
                </div>
              )}

              {/* Fecha y duración del curso (solo para capacitaciones) */}
              {!esCertificadoTramite && (verificacion.curso_fecha || verificacion.curso_duracion) && (
                <div className="grid grid-cols-2 gap-4 border-b pb-4">
                  {verificacion.curso_fecha && (
                    <div>
                      <p className="text-sm text-gray-500 mb-1">Fecha del curso</p>
                      <p className="font-semibold text-gray-900 flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-gray-400" />
                        {formatDate(verificacion.curso_fecha)}
                      </p>
                    </div>
                  )}
                  {verificacion.curso_duracion && (
                    <div>
                      <p className="text-sm text-gray-500 mb-1">Duración</p>
                      <p className="font-semibold text-gray-900 flex items-center gap-2">
                        <Clock className="w-4 h-4 text-gray-400" />
                        {verificacion.curso_duracion} horas
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Fecha de emisión */}
              <div>
                <p className="text-sm text-gray-500 mb-1">Fecha de emisión</p>
                <p className="font-semibold text-gray-900">
                  {formatDate(verificacion.fecha_emision)}
                </p>
              </div>

              <div className="bg-gray-50 rounded-lg p-4 mt-4">
                <p className="text-xs text-gray-500 text-center">
                  Emitido por el Colegio Profesional de Trabajadorxs Sociales<br />
                  Distrito Lomas de Zamora
                </p>
              </div>
            </div>
          )}

          {error && (
            <div className="p-6">
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-600 text-center">
                  Si creés que esto es un error, contactá al Colegio para verificar 
                  la autenticidad del documento.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="text-center mt-8">
          <Link
            to="/"
            className="text-blue-600 hover:underline text-sm"
          >
            Ir al inicio
          </Link>
        </div>
      </main>
    </div>
  );
}
